import React, { useState } from 'react'

import dayjs from 'dayjs'

import Validations from './Validations'
import {
  Button,
  DateCalendar,
  DragAndDrop,
  InlineMenu,
  Input,
  Textarea,
} from '../..'
import { useUser } from '../../../context'
import {
  FormActions,
  useProfileForm,
} from '../../../context/ProfileFormController'
import { FormFieldItem } from '../../../types'
import { convertToSpanish } from '../../../utilFunctions/getRole'
import { capitalizeFirstLetter } from '../../../utilFunctions/transformFunc'
import FormField, { Label } from '../../FormField'
import { PasswordInput } from '../../inputs'
import { ImageContainer } from '../styles'

const DynamicField = ({
  item,
  fieldFn,
  disabled,
  refId,
}: {
  item: FormFieldItem
  fieldFn: FormActions
  disabled: boolean
  refId?: string
}) => {
  const { userRole, onEditionMode } = useUser()
  const { isNewUser } = useProfileForm()

  const inNewUser = isNewUser ?? onEditionMode

  const { updateInput, updateState, handleCoverUpload, handleCredentials } =
    fieldFn

  const [showMandatoryError, setShowMandatoryError] = useState(false)

  const mandatoryField = (value: string | number | string[] | null) => {
    if (Array.isArray(value)) return value.length === 0
    const v = String(value ?? '').trim()
    return v.length === 0
  }

  const commonInputProps = {
    name: item.id,
    id: item.id,
    type: item.type,
    placeholder: item.placeholder,
    value: item.value as string,
    'aria-label': item.label,
    inputClass: item.inputClass ? `${item.inputClass} input` : 'input',
    onBlur: () => {
      if (!item.isMandatory) return
      const hasError = mandatoryField(item.value as string)
      setShowMandatoryError(hasError)
    },
  }

  const extraProps = item.type === 'number' ? { min: 0 } : {}

  const field = () => {
    switch (item.type) {
      case 'file':
        return (
          <ImageContainer>
            <DragAndDrop
              type="image"
              image={item.value as string | null}
              handleImageChange={updateState}
              onEditionMode={!disabled}
              refId={refId ?? item.id}
              handleFileUpload={handleCoverUpload}
              bgImage="/assets/profile.png"
            />
          </ImageContainer>
        )
      case 'menu':
        return (
          <InlineMenu
            userType={item.userType ?? userRole}
            menuName={item.label}
            activeFilter={item.value as string | string[] | null}
            setActiveFilter={(value) => updateState(item.id, value)}
            isDisabled={disabled}
            isProfile={true}
          />
        )
      case 'buttons':
        return (
          <div className="buttons">
            {item.options?.map((opt) => (
              <Button
                key={opt}
                onClick={() => updateState(item.id, opt)}
                className={item.value === opt ? 'active' : ''}
                label={capitalizeFirstLetter(convertToSpanish(opt))}
                disabled={disabled}
              />
            ))}
          </div>
        )
      case 'link':
        return (
          <>
            {!inNewUser ? (
              <div className="dropbox-link">
                <a href={item.value as string}>{item.label}</a>
              </div>
            ) : (
              <Input {...commonInputProps} disabled={disabled} />
            )}
          </>
        )
      case 'text':
        return (
          <Textarea
            {...commonInputProps}
            onChange={updateInput}
            readOnly={disabled}
            height={110}
          />
        )
      case 'date':
        return (
          <DateCalendar
            id={item.id}
            referenceDate={item.value as string}
            handleDate={(date) => {
              if (!date) {
                // setShowMandatoryError(true)
                return
              }
              const _date = dayjs(date).format('DD-MM-YYYY')
              updateState(item.id, _date)
            }}
            defaultValue={dayjs(item.value as string)}
            disabled={disabled}
            disableFuture={item.disableFuture}
          />
        )
      case 'email':
        return (
          <Input
            {...commonInputProps}
            onChange={handleCredentials}
            disabled={disabled}
          />
        )
      case 'password':
        return (
          <PasswordInput
            fieldName={item.id}
            value={item.value as string}
            handleChange={handleCredentials}
            disabledInput={disabled}
          />
        )
      default:
        return (
          <Input
            onChange={updateInput}
            {...commonInputProps}
            {...extraProps}
            disabled={disabled}
          />
        )
      // <div>{item.type} - Not implemented yet</div>
    }
  }

  return (
    <>
      {item.type === 'file' ||
      item.type === 'buttons' ||
      item.type === 'menu' ||
      item.type === 'link' ? (
        <>{field()}</>
      ) : (
        <FormField
          id={item.id}
          label={item.label}
          labelClass={
            item.id === 'yearsOfExperience' ? 'empty-line' : undefined
          }
        >
          <>
            {field()}
            {item.subText && <Label>{item.subText}</Label>}
            {!disabled && (
              <Validations
                value={item.value as string}
                validation={item.validation}
                showMandatory={item.isMandatory && showMandatoryError}
              />
            )}
          </>
        </FormField>
      )}
    </>
  )
}

export default DynamicField
